import styled, { css } from 'styled-components'
import { COLORS } from 'consts/colors'
import { MOBILE_SIZE_BREAKPOINT } from 'consts'
import { DURATIONS, EASING, RADII } from 'styles/consts'
import {
  customizeVerticalScrollbarThumbMixin,
  setCommonBorderStyles
} from 'styles/mixins'

export const EditorWrapper = styled.div`
  position: relative;
  width: 100%;
`

// The non-animated placeholder reuses the typewriter's overlay so both line up
// with where real input appears.
export { Overlay as PlaceholderOverlay } from '../AnimatedPlaceholder/styled'

/**
 * Mirrors common-components/TextAreaInput's field styles so swapping the
 * textarea for this contenteditable is visually invisible. Chips are created
 * imperatively (the content is not React-rendered), so they are styled here
 * as nested classes rather than styled components.
 */
export const Editor = styled.div<{
  $minHeight?: number
  $maxHeight?: number
  $maxHeightMobile?: number
  $isError?: boolean
}>`
  padding: 12px;
  width: 100%;
  min-height: ${({ $minHeight }) => ($minHeight ? `${$minHeight}px` : '105px')};
  max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : 'none')};
  overflow-y: auto;

  ${({ $maxHeightMobile }) =>
    $maxHeightMobile &&
    css`
      @media (max-width: ${MOBILE_SIZE_BREAKPOINT}px) {
        max-height: ${$maxHeightMobile}px;
      }
    `}

  /* Field tokens, same as TextAreaInput — the 4px radius this shipped with
     predates the design-system refresh and left the prompt box visibly squarer
     than every other input. */
  border-radius: ${RADII.field};
  background-color: ${COLORS.surface};
  color: ${COLORS.textPrimary};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  white-space: pre-wrap;
  overflow-wrap: break-word;

  ${customizeVerticalScrollbarThumbMixin()}
  ${({ $isError }) => setCommonBorderStyles($isError, false)}

  transition:
    color ${DURATIONS.base} ${EASING.out},
    border-color ${DURATIONS.base} ${EASING.out},
    box-shadow ${DURATIONS.base} ${EASING.out};

  &:focus {
    outline: none;
  }

  /* Colored-selection treatment (primaryTint fill + primary text), the same one
     the refresh gives active tabs and selected rows. This shipped on
     COLORS.primaryLight, which the refresh remapped from violet to grey — so the
     chips read as plain grey pills rather than references. */
  .${'mention-chip'} {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 4px 0 8px;
    margin: 0 1px;
    border-radius: ${RADII.pill};
    background: ${COLORS.primaryTint};
    color: ${COLORS.primary};
    font-weight: 600;
    font-size: 13px;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    vertical-align: baseline;
  }

  .${'mention-chip-remove'} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;

    /* A primary echo rather than a grey token: on the violet chip fill a grey
       hover reads as a smudge. (Same rgba(65, 54, 193, x) pattern the refresh
       uses for the subsidebar flash and the upgrade pulse.) */
    &:hover {
      background: rgba(65, 54, 193, 0.16);
    }
  }
`

/**
 * Zero-size element positioned at the caret (or the clicked chip); the portal
 * Flyout anchors to it. Because useRelativeCoords recomputes on anchor
 * IDENTITY (not position), the caller remounts this via `key` whenever the
 * caret moves.
 */
export const PickerAnchor = styled.div`
  position: absolute;
  width: 0;
  height: 0;
`
